/**
 * Whether this site may use the microphone: the browser's answer, followed.
 *
 * The phone asks `requestRecordingPermissions()` the moment a call opens, and
 * so does the browser — `getUserMedia` is the ask — so no surface needs this to
 * work. It is for the surfaces that draw ahead of the ask: the microphone-off
 * state before a person taps again, and a page that says what will happen.
 *
 *   prompt       the browser will ask
 *   granted      it will not ask again
 *   denied       it refused, and will refuse again until the person changes the
 *                site's setting in the browser: the microphone-off board
 *   unsupported  this browser cannot say (the Permissions API has no
 *                `microphone`); the ask itself is the only answer
 *
 * `askMicrophone` is the ask on its own: it opens the microphone and closes it
 * at once, and answers what the browser decided. `useLiveMicGate` is live
 * mode's gate on all of this.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { inAppBrowserOf, liveMicGateOf, type LiveMicBlock } from './live-edges';
import { micFailureOf } from './microphone';

export type MicPermission = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

const known = (state: PermissionState): MicPermission => state;

const permissionsApi = (): Permissions | undefined => (typeof navigator === 'undefined' ? undefined : navigator.permissions);

export const useMicPermission = (): MicPermission => {
  const [permission, setPermission] = useState<MicPermission>(() => (permissionsApi() === undefined ? 'unsupported' : 'unknown'));
  useEffect(() => {
    const permissions = permissionsApi();
    if (permissions === undefined) return undefined;
    let abandoned = false;
    let status: PermissionStatus | null = null;
    const follow = (): void => {
      if (!abandoned && status !== null) setPermission(known(status.state));
    };
    permissions
      .query({ name: 'microphone' as PermissionName })
      .then((answered) => {
        status = answered;
        follow();
        answered.addEventListener('change', follow);
      })
      .catch(() => {
        if (!abandoned) setPermission('unsupported');
      });
    return () => {
      abandoned = true;
      status?.removeEventListener('change', follow);
    };
  }, []);
  return permission;
};

/** Whether this page can reach a microphone at all: an insecure page, or an old browser, has no `getUserMedia`. */
export const hasMediaDevices = (): boolean => {
  const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  return devices !== undefined && typeof devices.getUserMedia === 'function';
};

/** The browser's answer, read once: what `useMicPermission` follows, for a decision that cannot wait for the hook. */
export const readMicPermission = async (): Promise<MicPermission> => {
  const permissions = permissionsApi();
  if (permissions === undefined) return 'unsupported';
  try {
    return known((await permissions.query({ name: 'microphone' as PermissionName })).state);
  } catch {
    return 'unsupported';
  }
};

/**
 * Live mode's gate on the microphone, for every way into it (Talk live, the
 * Ask OS orb, ⌘J, the Dashboard's door, setup's way in). `gate(go)` starts
 * live mode with `go` unless the browser refused the microphone (`blocked`
 * is then `mic_blocked`) or this page cannot reach one (`mic_unsupported`).
 * A way in that opens straight into live mode can run before the hook has
 * read the permission: the gate then reads it first, so a refusal is always
 * said as one, never as a microphone that would not start.
 */
export const useLiveMicGate = (): {
  readonly permission: MicPermission;
  readonly blocked: LiveMicBlock | null;
  readonly clear: () => void;
  readonly gate: (go: () => void, onBlocked?: () => void) => void;
} => {
  const permission = useMicPermission();
  const latest = useRef(permission);
  useEffect(() => {
    latest.current = permission;
  });
  const [blocked, setBlocked] = useState<LiveMicBlock | null>(null);
  const clear = useCallback(() => setBlocked(null), []);
  const gate = useCallback((go: () => void, onBlocked?: () => void): void => {
    setBlocked(null);
    const decide = (read: MicPermission): void => {
      const answer = liveMicGateOf({
        permission: read,
        mediaDevices: hasMediaDevices(),
        inApp: typeof navigator !== 'undefined' && inAppBrowserOf(navigator.userAgent) === 'in_app',
      });
      if (answer === 'go') {
        go();
        return;
      }
      setBlocked(answer);
      onBlocked?.();
    };
    const now = latest.current;
    // Read already: decided now, inside the click, so the browser still counts the call's audio as the person's.
    if (now !== 'unknown') decide(now);
    else void readMicPermission().then(decide);
  }, []);
  return { permission, blocked, clear, gate };
};

/** Ask for the microphone and give it straight back. Answers what the browser decided. */
export const askMicrophone = async (): Promise<'granted' | 'denied' | 'failed'> => {
  const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  if (devices === undefined || typeof devices.getUserMedia !== 'function') return 'failed';
  try {
    const stream = await devices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) track.stop();
    return 'granted';
  } catch (error) {
    return micFailureOf(error) === 'mic_off' ? 'denied' : 'failed';
  }
};
